import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { portalApi } from "@/lib/portalApi";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, FolderOpen, ChevronRight, FileText, Download, Phone, MessageSquare, CircleHelp } from "lucide-react";
import { FIRM_CONTACT } from "@/pages/portal/PortalHelp";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const parseDate = (d: string) => new Date(d.includes('T') ? d : d + 'T00:00:00');

const STATUS_LABELS: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  active: { label: "Active", variant: "default" },
  in_progress: { label: "In Progress", variant: "default" },
  on_hold: { label: "On Hold", variant: "secondary" },
  completed: { label: "Completed", variant: "outline" },
  cancelled: { label: "Cancelled", variant: "outline" },
};

const formatFileSize = (bytes: number) =>
  bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

function FirmDocuments() {
  const { toast } = useToast();
  const { data: documents = [] } = useQuery({
    queryKey: ["portal", "documents"],
    queryFn: () => portalApi.documents(),
  });

  if (documents.length === 0) return null;

  const handleDownload = async (docId: string) => {
    try {
      const { downloadUrl } = await portalApi.documentDownloadUrl(docId);
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch (err: any) {
      toast({ title: "Download failed", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Documents from Your Firm
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-3 py-2 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{doc.file_name}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(doc.uploaded_at), "MMM d, yyyy")}
                  {doc.file_size ? ` · ${formatFileSize(doc.file_size)}` : ""}
                  {doc.notes ? ` · ${doc.notes}` : ""}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDownload(doc.id)} title="Download">
                <Download className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default function PortalDashboard() {
  const [, setLocation] = useLocation();

  const { data: me } = useQuery({
    queryKey: ["portal", "me"],
    queryFn: () => portalApi.me(),
  });

  const { data: projects, isLoading, error, refetch } = useQuery({
    queryKey: ["portal", "projects"],
    queryFn: () => portalApi.projects(),
  });

  return (
    <PortalLayout accountName={me?.account_name}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Your Projects</h1>
          {me?.account_name && (
            <p className="text-muted-foreground mt-1">{me.account_name}</p>
          )}
        </div>

        {/* Contact strip — front and center so clients always know how to reach the firm */}
        <div className="rounded-lg border bg-card px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
          <a href={`tel:+1${FIRM_CONTACT.phone.replace(/\D/g, "")}`} className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <Phone className="w-3.5 h-3.5 text-primary" />
            {FIRM_CONTACT.phone}
          </a>
          <a href={`sms:+1${FIRM_CONTACT.textNumber.replace(/\D/g, "")}`} className="flex items-center gap-1.5 hover:text-primary transition-colors">
            <MessageSquare className="w-3.5 h-3.5 text-primary" />
            Text {FIRM_CONTACT.textNumber}
          </a>
          <span className="text-xs text-muted-foreground hidden sm:inline">{FIRM_CONTACT.hours}</span>
          <button
            onClick={() => setLocation("/portal/help")}
            className="flex items-center gap-1 text-primary hover:underline ml-auto text-xs font-medium"
          >
            <CircleHelp className="w-3.5 h-3.5" />
            Help &amp; Contact
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading projects…</span>
          </div>
        )}

        {error && (
          <div className="space-y-2">
            <p className="text-destructive text-sm">Failed to load projects. Please refresh.</p>
            <Button variant="outline" onClick={() => refetch()}>Refresh</Button>
          </div>
        )}

        {!isLoading && projects?.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
              <FolderOpen className="w-8 h-8" />
              <p>No active projects at this time.</p>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {(projects || []).map((project) => {
            const status = STATUS_LABELS[project.status] ?? { label: project.status, variant: "secondary" as const };
            const progress = project.task_count > 0
              ? Math.round((project.completed_task_count / project.task_count) * 100)
              : 0;

            return (
              <Card
                key={project.id}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setLocation(`/portal/project/${project.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLocation(`/portal/project/${project.id}`); } }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <CardTitle className="text-base font-semibold leading-snug">
                      {project.title}
                    </CardTitle>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={status.variant}>{status.label}</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                  {/* The period is what tells two same-titled projects apart, so it carries a
                      little more weight than the incidental metadata below. */}
                  {project.period && (
                    <p className="text-sm font-medium text-muted-foreground">{project.period}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {project.task_count > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Tasks</span>
                        <span>{project.completed_task_count} of {project.task_count} complete</span>
                      </div>
                      <Progress value={progress} className="h-1.5" />
                    </div>
                  )}
                  {project.due_date && (
                    <p className="text-sm text-muted-foreground">
                      Due {format(parseDate(project.due_date), "MMMM d, yyyy")}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <FirmDocuments />
      </div>
    </PortalLayout>
  );
}
